import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import Box from "@mui/material/Box";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { getAllSponsors, getStudyTypes } from "../../../services/sponsors";
import { createStudy, getAllPms } from "../../../services/studies";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DialogBox from "../../../components/DialogBox";
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt";

import "./Study.css";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/api-integration";
import { Autocomplete, Button, TextField } from "@mui/material";
import {
  buttonLinkStyle,
  rowStyles,
} from "../StudySetup/Visits/Questionnaires/EditVisitQuestionnaire";
import { handleBackNavigationGeneralFun } from "../../../components/helpers/helper";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#1890ff",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

const CreateStudy = () => {
  const [load, setLoad] = React.useState(false);

  const [sponsorList, setSponsorList] = React.useState([]);
  const [studyTypeList, setStudyTypeList] = React.useState([]);

  const [sponsor, setSponsor] = React.useState("");
  const [studyName, setStudyName] = React.useState("");
  const [studyType, setStudyType] = React.useState("");

  const [portUrl, setPortUrl] = React.useState("");
  const [apiUrl, setApiUrl] = React.useState("");
  const [questionaireUrl, setQuestionaireUrl] = React.useState("");
  const [projectMgrs, setProjectMgrs] = React.useState([]);
  const [managerSelected, setManagerSelected] = React.useState([]);
  const [isActive, setIsActive] = React.useState(true);
  const [searchedManager, setSearchedManager] = useState("");
  const [uncangedManagers, setUnchangedManagers] = useState([]);

  const [showDialog, setShowDialog] = React.useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const fetchSponsors = async () => {
    const res = await axios.get(
      `https://genesisapi.augursapps.com/app/Sponsor/SponsorList`
    );
    console.log({ res });
    setSponsorList(res?.data);
  };

  const fetchStudyTypes = async () => {
    const res = await getStudyTypes();
    console.log("study type", res.data);
    setStudyTypeList(res.data);
  };

  const pageNumber = 1;
  const pageSize = 10000;

  const getPMS = async () => {
    const res = await getAllPms(pageNumber, pageSize);
    // const temp = [];

    // res.data?.result?.map((manager) => {
    //   if (manager.studies.length === 0) {
    //     temp.push(manager);
    //   }
    // });

    // setProjectMgrs(temp);
    setProjectMgrs(res.data?.result);
    setUnchangedManagers(res.data?.result);
  };

  useEffect(() => {
    getPMS();
    fetchSponsors();
    fetchStudyTypes();
  }, []);

  const navigate = useNavigate();

  yup.addMethod(yup.string, "urlWithDomain", function () {
    return this.test(
      "urlWithDomain",
      "URL must be a valid URL with a domain extension",
      function (value) {
        const { path, createError } = this;
        if (!value) return true;
        const urlWithScheme = value.match(/^https?:\/\//i)
          ? value
          : `http://${value}`;
        try {
          const url = new URL(urlWithScheme);
          const validDomainPattern = /\.[a-z]{2,}$/i;
          if (!validDomainPattern.test(url.hostname)) {
            return createError({
              path,
              message: "URL must include a valid domain extension",
            });
          }

          return true;
        } catch {
          return createError({ path, message: "URL must be a valid URL" });
        }
      }
    );
  });

  const validationSchema = yup.object().shape({
    studyName: yup.string().max(255).required("Study Name is required"),
    portUrl: yup
      .string()
      .max(255)
      .required("Portal URL is required")
      .urlWithDomain(),
    apiUrl: yup
      .string()
      .max(255)
      .required("API URL is required")
      .urlWithDomain(),
    questionaireUrl: yup
      .string()
      .max(255)
      .required("Questionnaire builder URL is required")
      .urlWithDomain(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const notify = () =>
    toast.success("Study Added Successfully", {
      toastId: "createStudySuccessToast",
    });

  const sponsorRequired = () =>
    toast.warn("Sponsor Is Required", {
      toastId: "sponsorRequiredToast",
    });

  const studyTypeRequired = () =>
    toast.warn("Study Type Is Required", {
      toastId: "studyRequiredToast",
    });

  const managerRequired = () =>
    toast.warn("Project Manager Is Required", {
      toastId: "managerRequired",
    });

  const requestFailed = () =>
    toast.error("Something went wrong", {
      toastId: "requestFailed",
    });

  const cancelStudy = (e) => {
    e.preventDefault();

    if (showDialog) {
      const confirmExit = window?.confirm(
        "You have unsaved changes, Are you sure you want to leave?"
      );

      if (confirmExit) {
        navigate("/all-studies");
      }
    } else {
      navigate("/all-studies");
    }
  };

  const onSubmit = async (data, e) => {
    setLoad(true);
    e.preventDefault();
    try {
      if (sponsor === "") {
        sponsorRequired();
        setLoad(false);
      } else if (studyType === "") {
        studyTypeRequired();
        setLoad(false);
      } else if (managerSelected.length === 0) {
        managerRequired();
        setLoad(false);
      } else {
        setShowDialog(false);
        const newData = {
          SponsorId: sponsor,
          PortalUrl: data.portUrl,
          Name: data.studyName,
          ApiUrl: data.apiUrl,
          StudyTypeId: studyType,
          QuestionnaireBuilderUrl: data.questionaireUrl,
          ProjectManagerId: managerSelected,
          IsActive: true,
          StudyDbName: "db1",
          StudyDbJobId: 1,
        };

        const res = await createStudy(newData);
        if (res.status === 200) {
          setLoad(false);
          getPMS();
          fetchSponsors();
          fetchStudyTypes();
          notify();
          navigate("/all-studies");
        } else {
          setLoad(false);
          requestFailed();
        }
      }
    } catch (err) {
      console.log("submit error: ", err);
      setLoad(false);
      setShowDialog(true);
      requestFailed();
    }
  };

  const handleSponsorChange = (event) => {
    setSponsor(event.target.value);
    setShowDialog(true);
  };

  const handleStudyChange = (event) => {
    let name = event.target.value;
    if (name.length > 0 && name[0] === " ") {
      name = name.trimStart();
      event.target.value = name;
    }
    setStudyName(name);
    if (name) {
      setShowDialog(true);
      clearErrors("studyName");
    }
  };

  const handleStudyTypeChange = (event) => {
    setStudyType(event.target.value);
    setShowDialog(true);
  };

  const handlePortUrl = (event) => {
    let name = event.target.value;
    if (name.length > 0 && name[0] === " ") {
      name = name.trimStart();
      event.target.value = name;
    }
    setPortUrl(name);
    if (name) {
      setShowDialog(true);
      clearErrors("portUrl");
    }
  };

  const handleApiUrl = (event) => {
    let name = event.target.value;
    if (name.length > 0 && name[0] === " ") {
      name = name.trimStart();
      event.target.value = name;
    }
    setApiUrl(name);
    if (name) {
      setShowDialog(true);
      clearErrors("apiUrl");
    }
  };

  const handleQuestionaireUrl = (event) => {
    let name = event.target.value;
    if (name.length > 0 && name[0] === " ") {
      name = name.trimStart();
      event.target.value = name;
    }
    setQuestionaireUrl(name);
    if (name) {
      setShowDialog(true);
      clearErrors("questionaireUrl");
    }
  };

  const handleActiveChange = () => {
    setIsActive(!isActive);
    setShowDialog(true);
  };

  const handleMgrSelect = (item, event) => {
    const isChecked = event.target.checked;
    setShowDialog(true);
    if (isChecked === true) {
      const managerArray = [];
      managerArray.push(item.id);
      setManagerSelected([...managerSelected, ...managerArray]);
    } else if (isChecked === false) {
      const filterManager = managerSelected.filter((mgr) => mgr !== item.id);
      setManagerSelected(filterManager);
    }
  };

  const filterOptions = (options, state) => {
    return options.filter((option) =>
      option?.sponsorName
        ?.toLowerCase()
        ?.includes(state.inputValue.toLowerCase())
    );
  };

  const handleManagerSearch = (e) => {
    const value = e.target.value;
    setSearchedManager(value);
    const tempValue = value.toLocaleLowerCase();
    const searchedUnchangedManager = uncangedManagers?.filter((item) =>
      item?.emailAddress?.toLocaleLowerCase().includes(tempValue)
    );
    setProjectMgrs(searchedUnchangedManager);
  };

  const resetManagerSearch = () => {
    setSearchedManager("");
    setProjectMgrs(uncangedManagers);
  };

  return (
    <>
      {load ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <DialogBox
            showDialog={showPrompt}
            confirmNavigation={confirmNavigation}
            cancelNavigation={cancelNavigation}
          />

          <p className="admin-link" style={{ fontWeight: "600" }}>
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "homepage",
                  navigate
                )
              }
            >
              Home
            </button>
            {" | "}
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "all-studies",
                  navigate
                )
              }
            >
              Studies
            </button>
            {" | "}
            {/* <Link to="/create-study">Create Study</Link> */}
            <span
              style={{
                color: "#4b8ac0",
                cursor: "pointer",
              }}
            >
              Add Study
            </span>
          </p>
          <Box sx={{ marginTop: "2%" }}>
            <>
              <p className="sponsor-heading">Add Study</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="uploadField">
                  <Row style={{ alignItems: "center" }}>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Sponsor
                        <span className="error-highlight">*</span>
                      </label>
                      <Autocomplete
                        disablePortal
                        style={{ fontSize: "14px" }}
                        options={sponsorList}
                        onChange={(event, value) =>
                          setSponsor(value ? value.id : null)
                        }
                        getOptionLabel={(option) => option.sponsorName}
                        fullWidth
                        filterOptions={filterOptions}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            style={{ fontSize: "15px" }}
                            hiddenLabel={true}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                padding: "0",
                                borderRadius: "6px",
                              },
                            }}
                          />
                        )}
                        componentsProps={{
                          popper: {
                            sx: {
                              "& .MuiAutocomplete-listbox": {
                                fontSize: "13px",
                              },
                            },
                          },
                        }}
                      />
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Portal Url
                        <span className="error-highlight">*</span>
                      </label>
                      <input
                        className="nameSponsorField"
                        defaultValue={portUrl}
                        type="text"
                        name="portUrl"
                        {...register("portUrl", { required: true })}
                        onChange={handlePortUrl}
                      />
                      <span style={{ color: "#3661eb", marginTop: "1%" }}>
                        {errors.portUrl?.message}
                      </span>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Name
                        <span className="error-highlight">*</span>
                      </label>
                      <input
                        name="studyName"
                        defaultValue={studyName}
                        className="nameSponsorField"
                        type="text"
                        {...register("studyName", { required: true })}
                        onChange={handleStudyChange}
                      />
                      <span style={{ color: "#3661eb", marginTop: "1%" }}>
                        {errors.studyName?.message}
                      </span>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Api Url
                        <span className="error-highlight">*</span>
                      </label>
                      <input
                        className="nameSponsorField"
                        defaultValue={apiUrl}
                        type="text"
                        name="apiUrl"
                        {...register("apiUrl", { required: true })}
                        onChange={handleApiUrl}
                      />
                      <span style={{ color: "#3661eb", marginTop: "1%" }}>
                        {errors.apiUrl?.message}
                      </span>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Study Type
                        <span className="error-highlight">*</span>
                      </label>

                      <FormControl className="nameField">
                        <Select
                          name="studyType"
                          value={studyType}
                          inputProps={{
                            "aria-label": "Without label",
                            className: "custom-select-email-class",
                          }}
                          onChange={handleStudyTypeChange}
                        >
                          <MenuItem value="">
                            <em>Select Study Type</em>
                          </MenuItem>
                          {studyTypeList?.map((item, index) => (
                            <MenuItem key={index} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      <label className="uploadInputLabel">
                        Study Management Url{" "}
                        <span className="error-highlight">*</span>
                      </label>
                      <input
                        name="questionaireUrl"
                        defaultValue={questionaireUrl}
                        className="nameSponsorField"
                        type="text"
                        {...register("questionaireUrl", { required: true })}
                        onChange={handleQuestionaireUrl}
                      />
                      <span style={{ color: "#3661eb", marginTop: "1%" }}>
                        {errors.questionaireUrl?.message}
                      </span>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "1%" }}
                    >
                      {projectMgrs &&
                      uncangedManagers &&
                      projectMgrs.length === 0 &&
                      uncangedManagers?.length === 0 ? (
                        <p>No project managers available</p>
                      ) : (
                        <fieldset className="my-fieldset">
                          <legend className="manager-legend">
                            Project Managers:
                          </legend>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              width: "100%",
                            }}
                          >
                            <TextField
                              value={searchedManager}
                              placeholder="Search Project Manager"
                              style={{
                                width: "75%",
                                marginLeft: "5px",
                                marginBottom: "15px",
                                fontSize: "15px",
                              }}
                              onChange={(e) => {
                                handleManagerSearch(e);
                              }}
                              inputProps={{
                                style: {
                                  padding: "10px", // Adjust padding to make the input and button heights similar
                                  fontSize: "15px",
                                  padding: "8px 14px",
                                },
                              }}
                            ></TextField>
                            {searchedManager !== "" && (
                              <Button
                                variant="outlined"
                                style={{
                                  padding: "-10px 16px", // Adds proper padding for better alignment
                                  marginTop: "-15px",
                                }}
                                // className="pt-3"
                                onClick={() => {
                                  resetManagerSearch();
                                }}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="scrollable-content">
                            {projectMgrs.map((item, index) => (
                              <div className="toggleContainerNew" key={index}>
                                <p className="generalHead">
                                  {item.emailAddress}
                                </p>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <AntSwitch
                                    onChange={(event) =>
                                      handleMgrSelect(item, event)
                                    }
                                    inputProps={{ "aria-label": "ant design" }}
                                  />
                                </Stack>
                              </div>
                            ))}
                          </div>
                        </fieldset>
                      )}
                    </Col>
                    {/* <Col xs={12} sm={12} md={6} style={{paddingInline: "3%", paddingBlock: "1%"}}>
                      <fieldset className="my-fieldset">
                        <legend className="status-legend">Status:</legend>
                        <div className="toggleContainer">
                          <p className="generalHead">Active</p>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AntSwitch
                              checked={isActive}
                              onChange={handleActiveChange}
                              inputProps={{ "aria-label": "ant design" }}
                            />
                          </Stack>
                        </div>
                      </fieldset>
                    </Col> */}
                    {/* <Col
                      xs={12}
                      sm={12}
                      md={6}
                      style={{ paddingInline: "3%", paddingBlock: "4%" }}
                    >
                      <div className="createSponsor-buttons">
                        <button
                          className="sponsorCancelButton"
                          onClick={cancelStudy}
                        >
                          Cancel
                        </button>
                        <button className="sponsorCreateButton">Add</button>
                      </div>
                    </Col> */}
                  </Row>
                  <Row style={rowStyles}>
                    <Col md={8}></Col>
                    <Col md={2}>
                      <div className="study-management-head-end">
                        <button
                          onClick={(e) => {
                            cancelStudy(e);
                          }}
                          className="study-management-cancel-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </Col>
                    <Col md={2}>
                      <div className="study-management-head-end">
                        <button
                          type="submit"
                          className="study-management-create-btn-md"
                        >
                          Add
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </form>
            </>
          </Box>
        </div>
      )}
    </>
  );
};

export default CreateStudy;
